import React, { createContext, useContext, useState, useEffect } from "react"
import dayjs from "dayjs"
import { useAppCtx } from "lib/state"
import { appTools, cn } from "lib/utils"
import { useTemplatePreviewCtx } from "lib/units/template/preview/TemplatePreviewProvider";
import SectionHeaderRender from "../SectionHeader/SectionHeaderRender";
import { useRenderStyling } from "lib/hooks/useRenderStyling";

const DetailScreenContext = createContext({});
export const useDetailScreenCtx = () => useContext(DetailScreenContext);

function TrxDetailBlock({ showBorder=true, className="", children }:{ showBorder?:boolean; className?:string; children:React.ReactNode }){
  return (
    <div 
      style={{
        width: "100%",
        marginBottom: "0.25rem",
        display: "flex",
        flexDirection: "column",
        border: showBorder === true ? "1px solid #d1d5db" : "none",
      }}
    >
      { children}
    </div>
  )
}

function TrxDetailHeader({ title }:{ title:string }){
  return (
    <div 
      style={{
        width: "100%",
        height: "2.5rem",
        paddingLeft: "0.75rem",
        paddingRight: "0.75rem",
        borderBottom: "1px solid #d1d5db",
        backgroundColor: "#e5e7eb",
        display: "flex",
        alignItems: "center"
      }}
>
      <h3 
        style={{
          fontSize: "1rem",
          fontWeight: "600",
          textTransform: "uppercase",
          lineHeight: "1.5rem",
          paddingBottom: "0.125rem",
          borderBottom: "4px solid #000000",
          color: "#000000"
        }}
      >{title}</h3>
    </div>
  )
}

function TrxDetailLabel({ label, value, size="md", className="" }:{ label:string; value:any; size?:string; className?:string }){
  const { visualEffects }:any = useDetailScreenCtx()
  return (
    <span 
      style={{
        fontSize: size === "lg" ? "1rem" : "0.875rem",
        fontWeight: "500",
        color: "#000000",
        lineHeight: "1.2rem",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  )
}

function TrxDetailValue({ label, value, size="md", className="" }:{ label:string; value:any; size?:string; className?:string }){
  const { visualEffects }:any = useDetailScreenCtx()
  return (
    <span style={{
      fontSize: size === "lg" ? "1rem" : "0.875rem",
      fontWeight: "300",
      color: "#000000",
      lineHeight: "1.2rem",
      whiteSpace: "nowrap",
    }}>
      {value}
    </span>
  )
}

function TrxDataElement({ id, label, value, dir="row", size="md" }:{ id?:string; label:string; value:any; dir?:string; size?:string }){
  return (
    <div 
      id={id} 
      style={{
        display: "flex",
        flexDirection: dir === "col" ? "column" : "row",
        justifyContent: "space-between",
        alignItems: "flex-start",
        paddingTop: "0.15rem",
        paddingBottom: "0.15rem",
      }}
    >
      <TrxDetailLabel label={label} value={value} size={size} />
      <TrxDetailValue label={label} value={value} size={size} />
    </div>
  )
}


export default function DetailScreensRender({ visualEffects=[] }:{ visualEffects?:string[] }){

  const { mode, subdomain, getFromRp, formatter, getElementConfig }:any = useTemplatePreviewCtx() 
  const config = getElementConfig("detail_screens")
  
  const [ preppedData, setPreppedData ] = useState<any>({ ready: false })
  const [ spotlightBox, setSpotlightBox ] = useState<any>({ ready: false }) 

  const capitalize =(str:string) => str.charAt(0).toUpperCase() + str.slice(1)

  const caseId = getFromRp("Chargeback.ProcessorCaseId")
  const cardData = {
    authCode: getFromRp("RepresentmentTransactionInfo.AuthCode"),
    ccNumber: getFromRp("RepresentmentTransactionInfo.CardNumber"),
    ccExpDate: getFromRp("RepresentmentTransactionInfo.ExpDate"),
    cardBrand: getFromRp("RepresentmentTransactionInfo.CardBrand"),
    descriptor: getFromRp("Chargeback?.PriorRefund?.PriorAlert?.Descriptor") || ""
  }
  const customer = {
    billing: {
      name: getFromRp("OrderInfo.BillingContact.Name"),
      address: getFromRp("OrderInfo.BillingContact.Address"),
      city: getFromRp("OrderInfo.BillingContact.City"),
      state: getFromRp("OrderInfo.BillingContact.State"),
      zip: getFromRp("OrderInfo.BillingContact.Zip"),
      country: getFromRp("OrderInfo.BillingContact.Country"),
      phone: getFromRp("OrderInfo.Phone")
    },
    shipping: {
      name: getFromRp("OrderInfo.ShippingContact.Name") || getFromRp("OrderInfo.BillingContact.Name"),
      address: getFromRp("OrderInfo.ShippingContact.Address") || getFromRp("OrderInfo.BillingContact.Address"),
      city: getFromRp("OrderInfo.ShippingContact.City") || getFromRp("OrderInfo.BillingContact.City"),
      state: getFromRp("OrderInfo.ShippingContact.State") || getFromRp("OrderInfo.BillingContact.State"),
      zip: getFromRp("OrderInfo.ShippingContact.Zip") || getFromRp("OrderInfo.BillingContact.Zip"),
      country: getFromRp("OrderInfo.ShippingContact.Country") || getFromRp("OrderInfo.BillingContact.Country"),
      phone: getFromRp("OrderInfo.Phone")
    }
  }

  const prepData = () => {
    const transactions = getFromRp("AdditionalTransactions") || []
    const screens = transactions.map( (trx:any) => ({
      trxinfo: {
        merchant: trx.TransactionDetails.CompanyName,
        trxId: trx.TransactionDetails.ExternalTransactionId,
        entryMethod: "Keyed",
        respCode: "100",
        date: trx.TransactionDetails.TransactionDate,
        trxType: `${trx.TransactionDetails.ActionType.charAt(0).toUpperCase()}${trx.TransactionDetails.ActionType.slice(1)}`,
        trxAmount: trx.TransactionDetails.Amount,
        trxCurrency: trx.TransactionDetails.Currency,
        status: "Complete"
      },

      ccInfo: {
        ccNumber: cardData.ccNumber,
        ccType: cardData.cardBrand,
        cvvStatus: (trx.TransactionDetails.CVSMatchCode_Id === "M" || trx.TransactionDetails.CVSMatchCode_Id == "1") ? "CVV2/CVC2 Match" : "",
        ccExp: cardData.ccExpDate,
        avsStatus: getFromRp("RepresentmentTransactionInfo.TransactionAVSCode"),
        authCode: cardData.authCode,
      },

      billing: customer.billing,
      shipping: customer.shipping,

      additional: {
        orderId: trx.TransactionDetails.MerchantReference,
      },

      action: {
        success: trx.TransactionDetails.Success,
        actionType: trx.TransactionDetails.ActionType,
        date: trx.TransactionDetails.TransactionDate,
        amt: trx.TransactionDetails.Amount,
        currency: getFromRp("Chargeback.Currency"),
      }
    }))
    setPreppedData({ ready: true, screens })
  }

  const getMeasurements = () => {
    const container = document.getElementById("mm-detail-screens")
    const merchant_name_location = document.getElementById("merchant_name")
    if( container && merchant_name_location ){
      const containerWidth = container.clientWidth
      const containerHeight = container.clientHeight
      const merchant_name_location_top = merchant_name_location.offsetTop
      const merchant_name_location_height = merchant_name_location.clientHeight
      const merchant_name_location_width = merchant_name_location.clientWidth
      const availableHeight = containerHeight - (merchant_name_location_top + merchant_name_location_height)
      setSpotlightBox({ 
        top: merchant_name_location_top, 
        left: 0,
        width: merchant_name_location_width, 
        height: availableHeight,
        ready: true
      })
    }
  }

  const splitBlocksStyle = useRenderStyling("w-6/12 flex flex-col pl-4 pr-4 pt-0 pb-0", "styleObj")
  const blocksWrapperStyle = useRenderStyling("w-full flex gap-5", "styleObj")
  const userDataWrapper = useRenderStyling("w-6/12 flex flex-col pt-0 pb-0", "styleObj")
  const userDataStyle = useRenderStyling("w-full pl-3 pr-3 flex flex-col pt-1 pb-1", "styleObj")
  const actionsWrapper = useRenderStyling("w-full flex mt-4 justify-start items-start pb-3", "styleObj")
  const textBold = useRenderStyling("text-base font-bold", "styleObj")
  const textNormal = useRenderStyling("text-base font-normal", "styleObj")

  useEffect(() => {
    if( preppedData.ready ){
      getMeasurements()
    }
  },[preppedData])

  useEffect(() => {
    prepData()
  },[])

  if( !preppedData.ready ) return <></>
  return (
    <DetailScreenContext.Provider value={{ visualEffects }}>
      
      <SectionHeaderRender title="Transaction Information" />

      <div 
        id="mm-detail-screens" 
        style={{
          width: "100%",
          marginTop: "1rem",
          marginBottom: "1rem",
          display: "flex",
          flexDirection: "column",
          gap: "2.5rem",
          position: "relative",
          paddingLeft: "0.75rem",
          paddingRight: "0.75rem"
        }}
        className="mm-section">
        {/* { visualEffects.includes("mask") && (
          <div className="w-full h-full absolute top-16 left-0 bg-gray-500/10" />
        )}  */}

        { preppedData.screens.map( (screen:any, idx:number) => (
          <div style={{ display: "block", pageBreakInside: "avoid", breakInside: "avoid" }} key={idx}>
            <div 
              key={idx} 
              style={{
                width: "62.5%",
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
                border: "1px solid #d1d5db",
                padding: "0.25rem",
              }}
            >
            
              {/* { spotlightBox.ready === true && (
                <div 
                  className={cn("absolute z-20 border-4 border-solid border-neutral-700 bg-transparent",  
                  )}
                  style={{
                    top: `${spotlightBox.top}px`, 
                    left: `${spotlightBox.left}px`, 
                    width: `${spotlightBox.width}px`, 
                    height: `30px`
                  }}
                />
              )} */}


              <TrxDetailBlock>
                <TrxDetailHeader title="Transaction Information" />
                <div {...blocksWrapperStyle}>
                  {/* left side  */}
                  <div {...splitBlocksStyle}>
                    <TrxDataElement id="merchant_name" label="Merchant" value={screen.trxinfo.merchant} />
                    <TrxDataElement label="Transaction ID" value={screen.trxinfo.trxId} />
                    <TrxDataElement label="Entry Method" value={screen.trxinfo.entryMethod} />
                    <TrxDataElement label="Response Code" value={screen.trxinfo.respCode} />
                  </div>
                  {/* right side  */}
                  <div {...splitBlocksStyle}>
                    <TrxDataElement label="Date" value={ formatter.date(screen.trxinfo.date, "MMM DD, YYYY") } />
                    <TrxDataElement label="Transaction Type" value={screen.trxinfo.trxType} />
                    <TrxDataElement label="Transaction Amount" value={ formatter.currency(screen.trxinfo.trxAmount, screen.trxinfo.trxCurrency) } />
                    <TrxDataElement label="Status" value={screen.trxinfo.status} />
                  </div>
                </div>
              </TrxDetailBlock>

              <TrxDetailBlock>
                <TrxDetailHeader title="Credit Card Information" />
                <div {...blocksWrapperStyle}>
                  {/* left side  */}
                  <div {...splitBlocksStyle}>
                    <TrxDataElement label="CC Number" value={screen.ccInfo.ccNumber} />
                    <TrxDataElement label="CC Type" value={screen.ccInfo.ccType} />
                    <TrxDataElement label="CVV Status" value={screen.ccInfo.cvvStatus} />
                  </div>
                  {/* right side  */}
                  <div {...splitBlocksStyle}>
                    <TrxDataElement label="CC Expiration" value={screen.ccInfo.ccExp} />
                    <TrxDataElement label="AVS Status" value={screen.ccInfo.avsStatus} />
                    <TrxDataElement label="Auth Code" value={screen.ccInfo.authCode} />
                  </div>
                </div>
              </TrxDetailBlock>

              <div className={cn("w-full flex items-stretch")}>
                <div {...userDataWrapper}>
                  <TrxDetailBlock showBorder={false}>
                    <TrxDetailHeader title="Billing Information" />
                    <div {...userDataStyle}>
                      <TrxDetailValue label="" value={screen.billing.name} />
                      <TrxDetailValue label="" value={screen.billing.address} />
                      <TrxDetailValue label="" value={screen.billing.city} />
                      <TrxDetailValue label="" value={screen.billing.state} />
                      <TrxDetailValue label="" value={screen.billing.zip} />
                      <TrxDetailValue label="" value={screen.billing.country} />
                      <TrxDetailValue label="" value={screen.billing.phone} />
                    </div>
                  </TrxDetailBlock>
                </div>
                
                <div {...userDataWrapper}>
                  <TrxDetailBlock showBorder={false}>
                    <TrxDetailHeader title="Shipping Information" />
                    <div {...userDataStyle}>
                      <TrxDetailValue label="" value={screen.shipping.name} />
                      <TrxDetailValue label="" value={screen.shipping.address} />
                      <TrxDetailValue label="" value={screen.shipping.city} />
                      <TrxDetailValue label="" value={screen.shipping.state} />
                      <TrxDetailValue label="" value={screen.shipping.zip} />
                      <TrxDetailValue label="" value={screen.shipping.country} />
                      <TrxDetailValue label="" value={screen.shipping.phone} />
                    </div>
                  </TrxDetailBlock>
                </div>
              </div>

              <TrxDetailBlock>
                <TrxDetailHeader title="Actions" />
                <div {...actionsWrapper}>
                  
                  <div 
                    style={{
                      width: "75%",
                      display: "flex",
                      justifyContent: "flex-start",
                      alignItems: "flex-start",
                      gap: "0.25rem",
                      padding: "0.5rem 0.5rem",
                    }}
                  >
                    <div 
                      style={{
                        width: "16.66667%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "1.5rem",
                        backgroundColor: screen.action.actionType === "refund" ? "#fca5a5" : "#bbf7d0",
                        color: "#000000",
                        fontSize: "0.75rem",
                        borderRadius: "0.375rem",
                        textTransform: "uppercase",
                      }}
                    >
                      <span>{capitalize(screen.action.actionType)}</span>
                    </div>
                    <div style={{ width: "66.66667%", display: "flex", flexDirection: "column", paddingLeft: ".25rem" }}>
                      <span {...textBold}>Card {capitalize(screen.action.actionType)}</span>
                      <span {...textNormal}>{dayjs(screen.action.date).format("MMM DD, YYYY HH:MM:ss a")}</span>
                    </div>
                  </div>

                  <div style={{ width: "33.33333%", display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "flex-end", paddingRight: "0.5rem" }}>
                      <span {...textNormal}><span {...textBold}>{screen.action.amt}</span> {screen.action.currency}</span>
                      <div 
                        style={{
                          width: "66.66667%",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "1.5rem",
                          backgroundColor: "#93c5fd",
                          border: "1px solid #3b82f6",
                          borderRadius: "0.375rem",
                          color: "#000000",
                          fontSize: "0.75rem",
                          textTransform: "uppercase",
                        }}
                      >
                        <span>Show Details</span>
                      </div>
                    </div>
                </div>
              </TrxDetailBlock>
            
            </div>
          </div>
        ))}
      </div>
    </DetailScreenContext.Provider>
  )
}
